/**
 * <search-with-completion>: search input backed by a remote physicians API.
 *
 * Reads `data-fetch-url` and `data-fetch-min-chars`, debounces input, GETs
 * `{url}?search={query}`, and renders JSON `data[]` as selectable options.
 * Selecting an option swaps the field for a chip; "Remove" swaps back.
 *
 * Expected JSON shape:
 *   { success: true, data: [{ shopifyId, firstName, lastName, city, stateRegion }, ...] }
 */

const DEBOUNCE_MS = 300;

function mapPhysician(row) {
  const first = typeof row.firstName === 'string' ? row.firstName.trim() : '';
  const last = typeof row.lastName === 'string' ? row.lastName.trim() : '';
  const label = [first, last].filter(Boolean).join(' ') || '—';
  const city = typeof row.city === 'string' ? row.city.trim() : '';
  const region = typeof row.stateRegion === 'string' ? row.stateRegion.trim() : '';
  const subtitle = [city, region].filter(Boolean).join(', ');
  const value = typeof row.shopifyId === 'string' ? row.shopifyId : '';
  return { value, label, subtitle };
}

class SearchWithCompletion extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;

    this.input = this.querySelector('.search__input');
    this.listbox = this.querySelector('[role="listbox"]');
    if (!this.input || !this.listbox) return;

    this.scrollEl = this.listbox.querySelector('.search-with-completion__scroll');
    this.emptyEl = this.listbox.querySelector('[data-search-with-completion-empty]');
    this.iconEl = this.querySelector('.search__icon');
    this.valueField = this.querySelector('[data-search-with-completion-value]');
    this.labelField = this.querySelector('[data-search-with-completion-label-field]');
    this.subtitleField = this.querySelector('[data-search-with-completion-subtitle-field]');
    this.shellEl = this.querySelector('[data-search-with-completion-shell]');
    this.selectedEl = this.querySelector('[data-search-with-completion-selected]');
    this.selectedLabelEl = this.querySelector('[data-search-with-completion-selected-label]');
    this.selectedSubtitleEl = this.querySelector('[data-search-with-completion-selected-subtitle]');
    this.removeBtn = this.querySelector('[data-search-with-completion-remove]');

    this.minChars = parseInt(this.dataset.fetchMinChars, 10) || 3;
    this.fetchUrl = this.dataset.fetchUrl || '';
    this._activeIndex = -1;
    this._debounce = null;
    this._abort = null;

    this.input.setAttribute('role', 'combobox');
    this.input.setAttribute('aria-autocomplete', 'list');
    this.input.setAttribute('aria-controls', this.listbox.id);
    this.input.setAttribute('aria-expanded', 'false');
    this.input.setAttribute('aria-haspopup', 'listbox');

    this.input.addEventListener('focus', () => this._onFocus());
    this.input.addEventListener('input', () => this._onInput());
    this.input.addEventListener('keydown', (e) => this._onKeydown(e));
    this.listbox.addEventListener('mousedown', (e) => {
      if (e.target.closest('.search-with-completion__option')) e.preventDefault();
    });
    this.listbox.addEventListener('click', (e) => this._onOptionClick(e));
    this.removeBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      this._clearSelected();
    });
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) this._close();
    });

    this._ready = true;

    if (this._needsHydration()) this._hydrateFromValue();
  }

  _needsHydration() {
    const hasValue = !!(this.valueField && this.valueField.value);
    const hasLabel = !!(this.labelField && this.labelField.value);
    return hasValue && !hasLabel && !!this.fetchUrl;
  }

  async _hydrateFromValue() {
    const rawValue = this.valueField.value;
    // Accept either a full GID (gid://shopify/Customer/123) or a raw id; the
    // API treats the search param as a free-text match, so the numeric id is
    // enough to return the matching record.
    const lookupId = rawValue.includes('/') ? rawValue.split('/').pop() : rawValue;
    if (!lookupId) return;

    const sep = this.fetchUrl.includes('?') ? '&' : '?';
    const url = `${this.fetchUrl}${sep}search=${encodeURIComponent(lookupId)}`;
    try {
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const body = await res.json();
      const rows = body && body.success === true && Array.isArray(body.data) ? body.data : [];
      if (!rows.length || !this.isConnected) return;
      const match = rows.find((row) => row && row.shopifyId === rawValue) || rows[0];
      const { label, subtitle } = mapPhysician(match);
      if (!label || label === '—') return;
      this._selectOption(rawValue, label, subtitle);
    } catch (_err) {
      // Silently fall back to the default (empty) state; the hidden value is
      // preserved so the cart attribute still submits correctly.
    }
  }

  _options() {
    return Array.from(this.scrollEl.querySelectorAll('.search-with-completion__option'));
  }

  _setActive(index) {
    const options = this._options();
    options.forEach((btn) => {
      btn.setAttribute('aria-selected', 'false');
      btn.removeAttribute('data-active');
    });
    this._activeIndex = index;
    if (index < 0 || index >= options.length) {
      this.input.removeAttribute('aria-activedescendant');
      return;
    }
    const current = options[index];
    current.setAttribute('aria-selected', 'true');
    current.setAttribute('data-active', '');
    if (current.id) this.input.setAttribute('aria-activedescendant', current.id);
  }

  _open() {
    if (!this.listbox.hidden) return;
    this.listbox.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  _close() {
    this.listbox.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this._setActive(-1);
    if (this.emptyEl) this.emptyEl.hidden = true;
    this._setLoading(false);
  }

  _setLoading(loading) {
    this.toggleAttribute('data-loading', loading);
    if (this.iconEl) {
      this.iconEl.classList.toggle('icon-search', !loading);
      this.iconEl.classList.toggle('icon-arrows-clockwise', loading);
    }
    if (loading && this.emptyEl) this.emptyEl.hidden = true;
  }

  _renderOptions(items) {
    this.scrollEl.replaceChildren();
    items.forEach((item, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'search-with-completion__option';
      btn.setAttribute('role', 'option');
      btn.setAttribute('aria-selected', 'false');
      btn.tabIndex = -1;
      btn.id = `${this.input.id}-opt-${i}`;
      btn.dataset.value = item.value;
      btn.dataset.label = item.label;
      btn.dataset.subtitle = item.subtitle || '';

      const title = document.createElement('span');
      title.className = 'search-with-completion__option-title ep-body ep-body--regular';
      title.textContent = item.label;
      btn.appendChild(title);

      if (item.subtitle) {
        const sub = document.createElement('span');
        sub.className = 'search-with-completion__option-subtitle ep-body ep-body--small ep-body--light';
        sub.textContent = item.subtitle;
        btn.appendChild(sub);
      }

      this.scrollEl.appendChild(btn);
    });

    this.scrollEl.toggleAttribute('data-empty', items.length === 0);
    if (this.emptyEl) this.emptyEl.hidden = items.length > 0;
    this._setActive(-1);
  }

  _onFocus() {
    if (this.input.value.trim().length >= this.minChars) {
      this._scheduleFetch();
      this._open();
    }
  }

  _onInput() {
    const q = this.input.value.trim();
    if (q.length < this.minChars) {
      clearTimeout(this._debounce);
      this._abort?.abort();
      this.scrollEl.replaceChildren();
      this._setLoading(false);
      this._close();
      return;
    }
    this._scheduleFetch();
    this._open();
  }

  _onKeydown(e) {
    const options = this._options();
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (this.listbox.hidden) this._open();
      this._setActive(Math.min(this._activeIndex + 1, options.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (this.listbox.hidden) this._open();
      this._setActive(Math.max(this._activeIndex - 1, 0));
    } else if (e.key === 'Enter') {
      if (!this.listbox.hidden && options[this._activeIndex]) {
        e.preventDefault();
        options[this._activeIndex].click();
      }
    } else if (e.key === 'Escape' && !this.listbox.hidden) {
      e.preventDefault();
      this._close();
    }
  }

  _onOptionClick(e) {
    const btn = e.target.closest('.search-with-completion__option');
    if (!btn) return;
    this._selectOption(btn.dataset.value, btn.dataset.label, btn.dataset.subtitle);
  }

  _scheduleFetch() {
    clearTimeout(this._debounce);
    this.scrollEl.replaceChildren();
    this._setLoading(true);
    this._debounce = setTimeout(() => this._fetch(this.input.value.trim()), DEBOUNCE_MS);
  }

  async _fetch(query) {
    this._abort?.abort();
    const ac = new AbortController();
    this._abort = ac;
    const sep = this.fetchUrl.includes('?') ? '&' : '?';
    const url = `${this.fetchUrl}${sep}search=${encodeURIComponent(query)}`;

    try {
      const res = await fetch(url, { signal: ac.signal, headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      if (ac.signal.aborted || !this.isConnected) return;
      const rows = body && body.success === true && Array.isArray(body.data) ? body.data : [];
      this._setLoading(false);
      this._renderOptions(rows.map(mapPhysician));
    } catch (err) {
      if (err?.name === 'AbortError') return;
      if (!this.isConnected) return;
      this._setLoading(false);
      this._renderOptions([]);
    }
  }

  _setField(field, value) {
    if (!field) return;
    field.value = value;
    field.dispatchEvent(new Event('input', { bubbles: true }));
    field.dispatchEvent(new Event('change', { bubbles: true }));
  }

  _selectOption(value, label, subtitle) {
    this._setField(this.valueField, value);
    this._setField(this.labelField, label);
    this._setField(this.subtitleField, subtitle || '');
    this.input.value = label;
    this._close();

    if (this.selectedLabelEl) this.selectedLabelEl.textContent = label;
    if (this.selectedSubtitleEl) {
      this.selectedSubtitleEl.textContent = subtitle || '';
      this.selectedSubtitleEl.hidden = !subtitle;
    }
    this.shellEl.hidden = true;
    this.selectedEl.hidden = false;
  }

  _clearSelected() {
    this._setField(this.valueField, '');
    this._setField(this.labelField, '');
    this._setField(this.subtitleField, '');
    this.input.value = '';
    this.scrollEl.replaceChildren();
    this._close();
    this.selectedEl.hidden = true;
    this.shellEl.hidden = false;
    this.input.focus();
  }
}

if (!customElements.get('search-with-completion')) {
  customElements.define('search-with-completion', SearchWithCompletion);
}
